export interface SystemField {
  key: string;
  label: string;
  required: boolean;
  aliases?: string[];
}

export interface FieldMapping {
  [systemField: string]: string | null;
}

export const CONTACT_SYSTEM_FIELDS: SystemField[] = [
  { key: 'email', label: 'E-mail', required: true, aliases: ['e-mail', 'mail', 'correo', 'e_mail'] },
  { key: 'first_name', label: 'Nome', required: true, aliases: ['nome', 'name', 'firstname', 'first'] },
  { key: 'last_name', label: 'Sobrenome', required: false, aliases: ['sobrenome', 'lastname', 'surname', 'last'] },
  { key: 'company', label: 'Empresa', required: false, aliases: ['empresa', 'organization', 'org'] },
  { key: 'position', label: 'Cargo', required: false, aliases: ['cargo', 'job', 'title', 'posicao'] },
  { key: 'cell_phone', label: 'Telefone Celular', required: false, aliases: ['celular', 'mobile', 'cell', 'telefono_celular', 'phone_number_id'] },
  { key: 'phone', label: 'Telefone', required: false, aliases: ['telefone', 'telephone', 'fixo'] },
  { key: 'website', label: 'Site', required: false, aliases: ['site', 'url', 'web'] },
  { key: 'city', label: 'Cidade', required: false, aliases: ['cidade', 'ciudad'] },
  { key: 'state', label: 'Estado', required: false, aliases: ['estado', 'state_province_region', 'uf', 'province', 'region'] },
  { key: 'region', label: 'Região', required: false, aliases: ['regiao', 'region'] },
  { key: 'postal_code', label: 'CEP', required: false, aliases: ['cep', 'zip', 'zipcode', 'postalcode', 'postal'] },
  { key: 'country', label: 'País', required: false, aliases: ['pais', 'country', 'pais'] },
  { key: 'address_line_1', label: 'Endereço 1', required: false, aliases: ['endereco', 'address', 'addressline1', 'address1', 'rua'] },
  { key: 'address_line_2', label: 'Endereço 2', required: false, aliases: ['complemento', 'addressline2', 'address2'] },
  { key: 'external_id', label: 'ID Externo', required: false, aliases: ['id_externo', 'externalid', 'external'] },
  { key: 'anonymous_id', label: 'ID Anônimo', required: false, aliases: ['id_anonimo', 'anonymousid', 'anonymous'] },
];

const normalizeText = (text: string): string => {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '');
};

export const autoMapFields = (
  csvHeaders: string[],
  systemFields: SystemField[]
): FieldMapping => {
  const mapping: FieldMapping = {};
  
  systemFields.forEach(field => {
    const normalizedKey = normalizeText(field.key);
    const allPossibleNames = [field.key, ...(field.aliases || [])];
    
    const match = csvHeaders.find(header => {
      const normalizedHeader = normalizeText(header);
      
      return allPossibleNames.some(name => {
        const normalizedName = normalizeText(name);
        return normalizedHeader === normalizedName || 
               normalizedHeader.includes(normalizedName) ||
               normalizedName.includes(normalizedHeader);
      });
    });
    
    if (match) {
      mapping[field.key] = match;
    } else {
      mapping[field.key] = null;
    }
  });
  
  return mapping;
};

export const validateMapping = (
  mapping: FieldMapping,
  systemFields: SystemField[]
): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  systemFields.forEach(field => {
    if (field.required && !mapping[field.key]) {
      errors.push(`Campo obrigatório "${field.label}" não está mapeado`);
    }
  });
  
  return {
    valid: errors.length === 0,
    errors
  };
};

export const applyMapping = (
  csvRow: Record<string, string>,
  mapping: FieldMapping
): Record<string, string> => {
  const mappedData: Record<string, string> = {};
  
  Object.entries(mapping).forEach(([systemField, csvColumn]) => {
    if (csvColumn && csvRow[csvColumn] !== undefined) {
      mappedData[systemField] = csvRow[csvColumn];
    }
  });
  
  return mappedData;
};
